import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, HardHat, Briefcase, GraduationCap, ChevronRight, BadgeCheck, TrendingUp, Shield, Clock, Palette, Database, Users, PieChart, Clipboard, FileText, Lightbulb, Wrench, HandshakeIcon, BarChart } from 'lucide-react';
import './SolutionsPage.css';
import { Link } from 'react-router-dom';

const industries = [
  {
    icon: Building2,
    title: "Engineering & Construction",
    description: "Our primary focus: Streamline project documentation, automate RFP responses, and manage engineering requirements efficiently.",
    featured: true,
    benefits: [
      {
        title: "Faster Bid Response",
        description: "Cut RFP response time with AI-powered document analysis and automated proposal generation."
      },
      {
        title: "Requirements Management",
        description: "Automatically extract and track engineering requirements from project documents and specifications."
      },
      {
        title: "Document Control",
        description: "Centralize and version control all project documentation with intelligent search capabilities."
      }
    ],
    caseStudy: {
      title: "How a Global Construction Firm Accelerated Project Delivery",
      description: "A leading construction company implemented Ufficient to streamline their document management and project workflows, resulting in significantly improved efficiency."
    },
    detailedContent: [
      {
        title: "Project Knowledge Management",
        description: "Centralize all project documentation in a single, searchable knowledge base that understands engineering terminology and specifications.",
        icon: Database,
        benefits: [
          "Instant retrieval of project specifications and requirements",
          "Automatic version control and document history",
          "Intelligent tagging and categorization of engineering documents"
        ]
      },
      {
        title: "Automated RFP Response",
        description: "Leverage AI to quickly generate accurate proposal responses by automatically finding and extracting relevant information from previous projects.",
        icon: FileText,
        benefits: [
          "Identify similar past projects to use as references",
          "Automatically extract relevant experience and qualifications",
          "Generate draft proposals with proper formatting"
        ]
      }
    ]
  },
  {
    icon: Briefcase,
    title: "Professional Services",
    description: "Enhance knowledge sharing, improve proposal win rates, and accelerate client deliverables.",
    featured: false,
    benefits: [
      {
        title: "Knowledge Reuse",
        description: "Find and leverage past work to improve quality and consistency of deliverables."
      },
      {
        title: "Proposal Success",
        description: "Increase win rates by leveraging successful past proposals and best practices."
      },
      {
        title: "Client Satisfaction",
        description: "Deliver faster responses to client queries with instant access to relevant information."
      }
    ],
    caseStudy: {
      title: "How a Consulting Firm Improved Client Response Time",
      description: "A leading management consulting firm implemented Ufficient to improve knowledge sharing and accelerate client response times."
    },
    detailedContent: [
      {
        title: "Enterprise Knowledge Sharing",
        description: "Break down silos by connecting expertise and insights across your organization, making it easy to find the right answers quickly.",
        icon: Users,
        benefits: [
          "Unified search across practice areas and departments",
          "Expert identification based on past project experience",
          "Knowledge preservation as professionals transition roles"
        ]
      },
      {
        title: "Client Intelligence",
        description: "Instantly access comprehensive client information including past projects, key contacts, and deliverables to strengthen relationships.",
        icon: PieChart,
        benefits: [
          "Complete client engagement history at your fingertips",
          "Cross-selling opportunities identification",
          "Preparation for client meetings in minutes, not hours"
        ]
      }
    ]
  },
  {
    icon: HardHat,
    title: "Manufacturing",
    description: "Optimize documentation management, streamline compliance, and improve knowledge transfer.",
    featured: false,
    benefits: [
      {
        title: "Standard Operating Procedures",
        description: "Keep SOPs up-to-date and easily accessible across your organization."
      },
      {
        title: "Quality Control",
        description: "Ensure consistent quality with instant access to specifications and procedures."
      },
      {
        title: "Training Efficiency",
        description: "Accelerate onboarding and training with intelligent knowledge access."
      }
    ],
    caseStudy: {
      title: "How a Leading Manufacturer Reduced Quality Issues",
      description: "A global manufacturer implemented Ufficient to ensure consistent access to the latest procedures and specifications."
    },
    detailedContent: [
      {
        title: "Process Documentation Management",
        description: "Maintain a single source of truth for all manufacturing processes, automatically updating related documents when changes occur.",
        icon: Clipboard,
        benefits: [
          "Version-controlled SOPs and work instructions",
          "Change management with automatic notifications",
          "Mobile access to procedures on the factory floor"
        ]
      },
      {
        title: "Quality Assurance Support",
        description: "Ensure every team member has access to the most current quality requirements and testing procedures to maintain consistent standards.",
        icon: Wrench,
        benefits: [
          "Instant access to quality specifications",
          "Automated compliance documentation",
          "Historical quality data analysis"
        ]
      }
    ]
  },
  {
    icon: GraduationCap,
    title: "Research & Development",
    description: "Accelerate innovation by connecting research insights across your organization.",
    featured: false,
    benefits: [
      {
        title: "Research Insights",
        description: "Discover connections across research documents and data sets automatically."
      },
      {
        title: "IP Protection",
        description: "Secure intellectual property with granular access controls and tracking."
      },
      {
        title: "Innovation Speed",
        description: "Reduce time to market by leveraging existing research and avoiding duplication."
      }
    ],
    caseStudy: {
      title: "How a Pharmaceutical Company Accelerated Drug Development",
      description: "A leading pharmaceutical firm used Ufficient to connect research data across departments, accelerating their development pipeline."
    },
    detailedContent: [
      {
        title: "Research Intelligence",
        description: "Connect siloed research data and documents to discover hidden patterns and relationships that drive breakthrough innovations.",
        icon: Lightbulb,
        benefits: [
          "Cross-discipline insight discovery",
          "Automatic relationship mapping between research topics",
          "Historical trend analysis for research focus areas"
        ]
      },
      {
        title: "IP Management",
        description: "Protect your intellectual property while making it accessible to authorized team members who need it for innovation.",
        icon: Shield,
        benefits: [
          "Secure access controls for sensitive IP",
          "Automatic tracking of IP usage and references",
          "Duplicate research prevention"
        ]
      }
    ]
  }
];

const IndustryCard = ({ icon: Icon, title, description, benefits, featured }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div 
      className={`industry-card ${featured ? 'featured-card' : ''} ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <div className="industry-header">
        <motion.div 
          className="industry-icon-wrapper"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300, damping: 10 }}
        >
          <Icon className="industry-icon" />
          <div className="industry-icon-glow" />
        </motion.div>
        <div>
          <h3 className="industry-title">{title}</h3>
        </div>
      </div>
      <p className="industry-description">{description}</p>

      <motion.div 
        className="industry-benefits"
        initial="collapsed"
        animate={isExpanded ? "expanded" : "collapsed"}
        variants={{
          expanded: { opacity: 1, height: "auto", marginTop: "1.5rem" },
          collapsed: { opacity: 0, height: 0, marginTop: 0 }
        }}
      >
        {benefits.map((benefit, index) => (
          <div key={index} className="benefit-item">
            <div className="benefit-header">
              <BadgeCheck className="benefit-check" />
              <h4 className="benefit-title">{benefit.title}</h4>
            </div>
            <p className="benefit-description">{benefit.description}</p>
          </div>
        ))}
      </motion.div>

      <motion.button
        className="industry-expand-button"
        onClick={() => setIsExpanded(!isExpanded)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {isExpanded ? 'Show Less' : 'See Benefits'}
        <ChevronRight className={`expand-icon ${isExpanded ? 'rotated' : ''}`} />
      </motion.button>
    </motion.div>
  );
};

const IndustryDetailSection = ({ industry, index }) => {
  const isEven = index % 2 === 0;
  
  return (
    <div className="industry-detail-section">
      <div className="container">
        <motion.div 
          className="industry-detail-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <industry.icon className="industry-detail-icon" />
          <h2>{industry.title} Solutions</h2>
        </motion.div>
        
        <div className="industry-case-study">
          <motion.div 
            className="case-study-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <div className="case-study-icon-wrapper">
              <BarChart className="case-study-icon" />
            </div>
            <div className="case-study-text">
              <h3>Case Study</h3>
              <h4>{industry.caseStudy.title}</h4>
              <p>{industry.caseStudy.description}</p>
            </div>
          </motion.div>
        </div>
        
        <div className="industry-detailed-content">
          {industry.detailedContent.map((content, contentIndex) => (
            <motion.div 
              key={contentIndex}
              className={`industry-content-section ${contentIndex % 2 !== 0 ? 'alternate' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 + (contentIndex * 0.1) }}
            >
              <div className="industry-content-grid">
                <div className={`industry-content-text ${contentIndex % 2 !== 0 ? 'order-last' : ''}`}>
                  <h3>{content.title}</h3>
                  <p>{content.description}</p>
                  <ul className="industry-feature-list">
                    {content.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex}>
                        <BadgeCheck size={18} />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`industry-content-image ${contentIndex % 2 !== 0 ? 'order-first' : ''}`}>
                  <div className="industry-feature-image-wrapper">
                    <content.icon size={48} className="industry-feature-icon" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SolutionsPage = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(0);

  return (
    <div className="solutions-page">
      <div className="solutions-hero">
        <motion.h1 
          className="solutions-title"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Ufficient for Your <span className="gradient-text">Industry</span>
        </motion.h1>
        <motion.p 
          className="solutions-description"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          Ufficient adapts to your industry's unique needs, with specialized solutions for construction and engineering teams. Our platform delivers measurable business value through intelligent knowledge management.
        </motion.p>
      </div>

      <div className="industries-grid">
        {industries.map((industry, index) => (
          <div key={index} onClick={() => setSelectedIndustry(index)}>
            <IndustryCard {...industry} />
          </div>
        ))}
      </div>

      {/* Detailed Industry Section */}
      <IndustryDetailSection industry={industries[selectedIndustry]} index={selectedIndustry} />

      <div className="solutions-cta">
        <h2>Not seeing your industry?</h2>
        <p>Contact us to learn how Ufficient can be customized for your specific needs.</p>
        <Link to='/contact'>
          <motion.button 
            className="solution-cta-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Schedule a Consultation
            <ChevronRight className="cta-arrow" />
          </motion.button>
        </Link>
      </div>
    </div>
  );
};

export default SolutionsPage;